import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { 
  ArrowLeft, Plus, Trash2, Save, Bot, MessageSquare, 
  Lightbulb, HelpCircle, FileText, Tag, Search, Pencil, Download
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORIES = [
  { id: "services", label: "Services", icon: FileText, color: "bg-blue-100 text-blue-700" },
  { id: "tarifs", label: "Tarifs & Prix", icon: Tag, color: "bg-green-100 text-green-700" },
  { id: "delais", label: "Délais", icon: MessageSquare, color: "bg-yellow-100 text-yellow-700" },
  { id: "zones", label: "Zones de livraison", icon: HelpCircle, color: "bg-purple-100 text-purple-700" },
  { id: "vip", label: "Programme VIP", icon: Lightbulb, color: "bg-pink-100 text-pink-700" },
  { id: "autre", label: "Autre", icon: Bot, color: "bg-gray-100 text-gray-700" },
];

export default function AdminSakuraKnowledge() {
  const navigate = useNavigate();
  const [knowledge, setKnowledge] = useState([]);
  const [loading, setLoading] = useState(true);
  const [initializing, setInitializing] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterCategory, setFilterCategory] = useState("all");
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({
    question: "",
    answer: "",
    category: "autre",
    keywords: ""
  });

  useEffect(() => {
    const auth = localStorage.getItem("adminAuth");
    if (!auth) {
      navigate("/admin");
      return;
    }
    fetchKnowledge();
  }, [navigate]);

  const fetchKnowledge = async () => {
    try {
      const auth = localStorage.getItem("adminAuth");
      const res = await axios.get(`${API}/admin/sakura-knowledge`, {
        headers: { Authorization: `Basic ${auth}` }
      });
      setKnowledge(res.data || []);
    } catch (error) {
      console.error("Error fetching knowledge:", error);
      toast.error("Erreur lors du chargement");
    } finally {
      setLoading(false);
    }
  };

  const handleInitialize = async () => {
    if (!window.confirm("Initialiser la base de connaissances avec les données par défaut (tarifs, services, conseils métier) ?")) return;
    
    setInitializing(true);
    try {
      const auth = localStorage.getItem("adminAuth");
      const res = await axios.post(`${API}/admin/sakura-knowledge/init`, {}, {
        headers: { Authorization: `Basic ${auth}` }
      });
      toast.success(res.data.message);
      fetchKnowledge();
    } catch (error) {
      console.error("Error initializing:", error);
      toast.error("Erreur lors de l'initialisation");
    } finally {
      setInitializing(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.question.trim() || !form.answer.trim()) {
      toast.error("Question et réponse requises");
      return;
    }

    try {
      const auth = localStorage.getItem("adminAuth");
      const payload = {
        ...form,
        keywords: form.keywords.split(",").map(k => k.trim()).filter(k => k)
      };

      if (editingId) {
        await axios.put(`${API}/admin/sakura-knowledge/${editingId}`, payload, {
          headers: { Authorization: `Basic ${auth}` }
        });
        toast.success("Connaissance mise à jour !");
      } else {
        await axios.post(`${API}/admin/sakura-knowledge`, payload, {
          headers: { Authorization: `Basic ${auth}` }
        });
        toast.success("Connaissance ajoutée !");
      }

      setForm({ question: "", answer: "", category: "autre", keywords: "" });
      setShowForm(false);
      setEditingId(null);
      fetchKnowledge();
    } catch (error) {
      console.error("Error saving knowledge:", error);
      toast.error("Erreur lors de la sauvegarde");
    }
  };

  const handleEdit = (item) => {
    setForm({
      question: item.question,
      answer: item.answer,
      category: item.category || "autre",
      keywords: (item.keywords || []).join(", ")
    });
    setEditingId(item.id);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Supprimer cette connaissance ?")) return;

    try {
      const auth = localStorage.getItem("adminAuth");
      await axios.delete(`${API}/admin/sakura-knowledge/${id}`, {
        headers: { Authorization: `Basic ${auth}` }
      });
      toast.success("Connaissance supprimée");
      fetchKnowledge();
    } catch (error) {
      console.error("Error deleting:", error);
      toast.error("Erreur lors de la suppression");
    }
  };

  const filteredKnowledge = knowledge.filter(item => {
    const matchesSearch = searchTerm === "" || 
      item.question.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.answer.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = filterCategory === "all" || item.category === filterCategory;
    return matchesSearch && matchesCategory;
  });

  const getCategoryInfo = (categoryId) => {
    return CATEGORIES.find(c => c.id === categoryId) || CATEGORIES[5];
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-pink-50 to-rose-100 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-pink-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-pink-50 to-rose-100">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-pink-100">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate("/admin/dashboard")}
                className="text-slate-600"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Retour
              </Button>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-gradient-to-br from-pink-500 to-rose-500 rounded-full flex items-center justify-center">
                  <Bot className="h-5 w-5 text-white" />
                </div>
                <div>
                  <h1 className="text-xl font-bold text-slate-800">Base de connaissances Sakura</h1>
                  <p className="text-sm text-slate-500">{knowledge.length} éléments</p>
                </div>
              </div>
            </div>
            <div className="flex gap-2">
              <Button 
                onClick={handleInitialize}
                disabled={initializing}
                variant="outline"
                className="border-pink-300 text-pink-700 hover:bg-pink-50"
              >
                <Download className="h-4 w-4 mr-2" />
                {initializing ? "Chargement..." : knowledge.length === 0 ? "Charger données de base" : "Réinitialiser données"}
              </Button>
              <Button 
                onClick={() => { setShowForm(true); setEditingId(null); setForm({ question: "", answer: "", category: "autre", keywords: "" }); }}
                className="bg-pink-600 hover:bg-pink-700"
              >
                <Plus className="h-4 w-4 mr-2" />
                Ajouter
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-8">
        {/* Info Card */}
        <Card className="mb-6 border-pink-200 bg-pink-50/50">
          <CardContent className="pt-6">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-full overflow-hidden border-2 border-pink-300">
                <img src="/sakura-avatar.png" alt="Sakura" className="w-full h-full object-cover" />
              </div>
              <div>
                <h3 className="font-semibold text-pink-800 mb-1">Comment ça marche ?</h3>
                <p className="text-sm text-pink-700">
                  Ajoute ici les questions fréquentes et leurs réponses. Sakura utilisera ces informations 
                  pour répondre aux clients de manière personnalisée. Plus tu ajoutes de détails, plus Sakura 
                  sera pertinente dans ses réponses !
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Form */}
        {showForm && (
          <Card className="mb-6 border-pink-200">
            <CardHeader>
              <CardTitle className="text-pink-800">
                {editingId ? "Modifier la connaissance" : "Nouvelle connaissance"}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-pink-700">Catégorie</Label>
                    <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                      <SelectTrigger className="border-pink-200">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {CATEGORIES.map(cat => (
                          <SelectItem key={cat.id} value={cat.id}>
                            <span className="flex items-center gap-2">
                              <cat.icon className="h-4 w-4" />
                              {cat.label}
                            </span>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-pink-700">Mots-clés (séparés par des virgules)</Label>
                    <Input
                      value={form.keywords}
                      onChange={(e) => setForm({ ...form, keywords: e.target.value })}
                      placeholder="tarif, prix, coût..."
                      className="border-pink-200"
                    />
                  </div>
                </div>
                
                <div>
                  <Label className="text-pink-700">Question / Sujet</Label>
                  <Input
                    value={form.question}
                    onChange={(e) => setForm({ ...form, question: e.target.value })}
                    placeholder="Ex: Quels sont vos tarifs ?"
                    className="border-pink-200"
                    required
                  />
                </div>

                <div>
                  <Label className="text-pink-700">Réponse de Sakura</Label>
                  <Textarea
                    value={form.answer}
                    onChange={(e) => setForm({ ...form, answer: e.target.value })}
                    placeholder="Ex: Nos tarifs dépendent du type de vêtement..."
                    rows={4}
                    className="border-pink-200"
                    required
                  />
                </div>

                <div className="flex gap-3">
                  <Button type="submit" className="bg-pink-600 hover:bg-pink-700">
                    <Save className="h-4 w-4 mr-2" />
                    {editingId ? "Mettre à jour" : "Ajouter"}
                  </Button>
                  <Button type="button" variant="outline" onClick={() => { setShowForm(false); setEditingId(null); }}>
                    Annuler
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        )}

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-4 mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Rechercher..."
              className="pl-10 border-pink-200"
            />
          </div>
          <Select value={filterCategory} onValueChange={setFilterCategory}>
            <SelectTrigger className="w-full sm:w-48 border-pink-200">
              <SelectValue placeholder="Catégorie" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Toutes les catégories</SelectItem>
              {CATEGORIES.map(cat => (
                <SelectItem key={cat.id} value={cat.id}>{cat.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Knowledge List */}
        <div className="grid gap-4">
          {filteredKnowledge.length === 0 ? (
            <Card className="border-pink-200">
              <CardContent className="py-12 text-center">
                <Bot className="h-12 w-12 mx-auto text-pink-300 mb-4" />
                <h3 className="text-lg font-medium text-slate-700 mb-2">
                  {knowledge.length === 0 ? "Aucune connaissance" : "Aucun résultat"}
                </h3>
                <p className="text-slate-500 mb-4">
                  {knowledge.length === 0 
                    ? "Commence par ajouter des informations pour Sakura !" 
                    : "Essaie avec d'autres mots-clés."}
                </p>
                {knowledge.length === 0 && (
                  <Button onClick={() => setShowForm(true)} className="bg-pink-600 hover:bg-pink-700">
                    <Plus className="h-4 w-4 mr-2" />
                    Ajouter une connaissance
                  </Button>
                )}
              </CardContent>
            </Card>
          ) : (
            filteredKnowledge.map((item) => {
              const catInfo = getCategoryInfo(item.category);
              return (
                <Card key={item.id} className="border-pink-100 hover:shadow-md transition-shadow">
                  <CardContent className="pt-6">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <Badge className={catInfo.color}>
                            <catInfo.icon className="h-3 w-3 mr-1" />
                            {catInfo.label}
                          </Badge>
                          {item.keywords?.length > 0 && (
                            <div className="flex gap-1">
                              {item.keywords.slice(0, 3).map((kw, i) => (
                                <Badge key={i} variant="outline" className="text-xs border-pink-200 text-pink-600">
                                  {kw}
                                </Badge>
                              ))}
                            </div>
                          )}
                        </div>
                        <h3 className="font-semibold text-slate-800 mb-2">{item.question}</h3>
                        <p className="text-slate-600 text-sm whitespace-pre-wrap">{item.answer}</p>
                      </div>
                      <div className="flex gap-2 shrink-0">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleEdit(item)}
                          className="text-pink-600 hover:text-pink-700 hover:bg-pink-50"
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleDelete(item.id)}
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })
          )}
        </div>
      </main>
    </div>
  );
}
